import Modal from "@/common/components/Modal"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm, SubmitHandler } from "react-hook-form"
import TextArea from "@/common/components/TextArea"
import { Dispatch, SetStateAction, useState } from "react"
import { assignPosSchema, AssignPosValidationSchema } from "../../schemas/assignPos.schema"
import SelectController from "@/common/components/SelectController"
import { useEstablishmentCompanies } from "@/features/establishments/hooks/useEstablishmentCompanies"
import { Mapper } from "@/common/services/mapper"
import { EstablishmentCompanyTransformer } from "@/features/establishments/services/establishmentCompanyTransformer"
import useDebounce from "@/common/hooks/useDebounce"
import { useAssignPos } from "@/features/pos/hooks/useAssignPos"
import { useParams } from "react-router-dom"

interface AssignPosModalProps {
  isOpen: boolean
  setIsOpen: Dispatch<SetStateAction<boolean>>
  onSuccess?: () => void
  onError?: () => void
}

const AssignPosModal = ({ isOpen, setIsOpen, onSuccess, onError }: AssignPosModalProps) => {
  const [serverError, setServerError] = useState(false)
  const { id } = useParams();
  const {
    control,
    register,
    handleSubmit,
    reset: resetForm,
    formState: { errors }
  } = useForm<AssignPosValidationSchema>({
    resolver: zodResolver(assignPosSchema)
  })

  const [searchEstablishmentCompanies, setSearchEstablishmentCompanies] = useDebounce('')

  const { data: establishmentCompaniesOptions } = useEstablishmentCompanies({
    filters: { search: searchEstablishmentCompanies },
    mapper: Mapper.mapWithTransformer({
      transformer: EstablishmentCompanyTransformer.optionTransformer({
        valueFieldName: 'matrix_branch_id',
        showEstablishmentCity: true,
      }),
    }),
  })

  const {mutate, isPending} = useAssignPos({
    id,
    onSuccess: () => {
      resetForm();
      setServerError(false);
      setIsOpen(false);
      onSuccess?.();
    },
    onError: () => {
      setServerError(true);
      onError?.();
    },
  });

  const onSubmit: SubmitHandler<AssignPosValidationSchema> = (data) => {
    mutate(data);
  }

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      onConfirm={handleSubmit(onSubmit)}
      title="Justificativa para associação"
      titleClass="text-2xl font-bold mb-2"
      subtitle="Preencha o motivo para associar esse POS a um estabelecimento"
      footer="Confirmar justificativa e salvar alteração"
      disabled={isPending}
      body={(
        <form className="flex flex-col gap-4">
          {serverError && <p className="text-sm text-danger-soft">Ocorreu um erro no servidor, cheque os campos e tente novamente</p>}
          <SelectController
            control={control}
            name="establishment_branch"
            label="Estabelecimento"
            errorMessage={errors?.establishment_branch?.message}
            options={establishmentCompaniesOptions}
            onInputChange={(value) => setSearchEstablishmentCompanies(value)}
          />
          <TextArea
            label="Justificativa"
            placeholder="Escreva aqui sua justificativa"
            errorMessage={errors?.description?.message}
            {...register('description')}
          />
        </form>
      )}
    />
  )
}

export default AssignPosModal